import React, { useState, useCallback } from 'react';
import useApi from './useApi';

const useForm = (initialValues = {}, validationRules = {}, onSubmit) => {
    const [values, setValues] = useState(initialValues);
    const [errors, setErrors] = useState({});
    const [touched, setTouched] = useState({});
    const { loading, error, execute } = useApi(onSubmit);

    const validate = useCallback(
        (fieldValues = values) => {
            const tempErrors = {};
            Object.keys(validationRules).forEach((key) => {
                const value = fieldValues[key];
                const rules = validationRules[key];

                if (rules.required && !value) {
                    tempErrors[key] = 'This field is required';
                } else if (rules.pattern && !rules.pattern.test(value)) {
                    tempErrors[key] = rules.message || 'Invalid format';
                } else if (rules.minLength && value.length < rules.minLength) {
                    tempErrors[key] = `Minimum length is ${rules.minLength}`;
                } else if (rules.maxLength && value.length > rules.maxLength) {
                    tempErrors[key] = `Maximum length is ${rules.maxLength}`;
                } else if (rules.custom) {
                    const customError = rules.custom(value, fieldValues);
                    if (customError) {
                        tempErrors[key] = customError;
                    }
                }
            });

            setErrors(tempErrors);
            return Object.keys(tempErrors).length === 0;
        },
        [values, validationRules]
    );

    const handleChange = useCallback(
        (e) => {
            const { name, value } = e.target;
            setValues((prev) => ({
                ...prev,
                [name]: value
            }));
            if (touched[name]) {
                validate({ ...values, [name]: value });
            }
        },
        [touched, validate, values]
    );

    const handleBlur = useCallback(
        (e) => {
            const { name } = e.target;
            setTouched((prev) => ({
                ...prev,
                [name]: true
            }));
            validate();
        },
        [validate]
    );

    const handleSubmit = useCallback(
        async (e) => {
            e.preventDefault();
            setTouched(
                Object.keys(values).reduce((acc, key) => {
                    acc[key] = true;
                    return acc;
                }, {})
            );

            if (validate()) {
                try {
                    await execute(values);
                    return true;
                } catch (err) {
                    return false;
                }
            }
            return false;
        },
        [execute, validate, values]
    );

    const resetForm = useCallback(() => {
        setValues(initialValues);
        setErrors({});
        setTouched({});
    }, [initialValues]);

    return {
        values,
        errors,
        touched,
        loading,
        error,
        handleChange,
        handleBlur,
        handleSubmit,
        resetForm,
        setValues
    };
};

export default useForm; 